/**
=========================================================
* Material Dashboard 2 PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from "@mui/material/styles";
// import Fade from "@mui/material/Fade";

// Material Dashboard 2 PRO React TS Base Styles
import borders from "app/themes/theme-dark/base/borders";
import boxShadows from "app/themes/theme-dark/base/boxShadows";
import breakpoints from "app/themes/theme-dark/base/breakpoints";
import colors from "app/themes/theme-dark/base/colors";
import globals from "app/themes/theme-dark/base/globals";
import typography from "app/themes/theme-dark/base/typography";

// Material Dashboard 2 PRO React TS Helper Functions
import boxShadow from "app/themes/theme-dark/functions/boxShadow";
import hexToRgb from "app/themes/theme-dark/functions/hexToRgb";
import linearGradient from "app/themes/theme-dark/functions/linearGradient";
import pxToRem from "app/themes/theme-dark/functions/pxToRem";
import rgba from "app/themes/theme-dark/functions/rgba";

// Material Dashboard 2 PRO React TS components base styles for @mui material components
import appBar from "app/themes/theme-dark/components/appBar";
import avatar from "app/themes/theme-dark/components/avatar";
import breadcrumbs from "app/themes/theme-dark/components/breadcrumbs";
import button from "app/themes/theme-dark/components/button";
import buttonBase from "app/themes/theme-dark/components/buttonBase";
import card from "app/themes/theme-dark/components/card";
import cardContent from "app/themes/theme-dark/components/card/cardContent";
import cardMedia from "app/themes/theme-dark/components/card/cardMedia";
import container from "app/themes/theme-dark/components/container";
import dialog from "app/themes/theme-dark/components/dialog";
import dialogActions from "app/themes/theme-dark/components/dialog/dialogActions";
import dialogContent from "app/themes/theme-dark/components/dialog/dialogContent";
import dialogContentText from "app/themes/theme-dark/components/dialog/dialogContentText";
import dialogTitle from "app/themes/theme-dark/components/dialog/dialogTitle";
import divider from "app/themes/theme-dark/components/divider";
import flatpickr from "app/themes/theme-dark/components/flatpickr";
import autocomplete from "app/themes/theme-dark/components/form/autocomplete";
import checkbox from "app/themes/theme-dark/components/form/checkbox";
import formControlLabel from "app/themes/theme-dark/components/form/formControlLabel";
import formLabel from "app/themes/theme-dark/components/form/formLabel";
import input from "app/themes/theme-dark/components/form/input";
import inputLabel from "app/themes/theme-dark/components/form/inputLabel";
import inputOutlined from "app/themes/theme-dark/components/form/inputOutlined";
import radio from "app/themes/theme-dark/components/form/radio";
import select from "app/themes/theme-dark/components/form/select";
import switchButton from "app/themes/theme-dark/components/form/switchButton";
import textField from "app/themes/theme-dark/components/form/textField";
import icon from "app/themes/theme-dark/components/icon";
import iconButton from "app/themes/theme-dark/components/iconButton";
import linearProgress from "app/themes/theme-dark/components/linearProgress";
import link from "app/themes/theme-dark/components/link";
import list from "app/themes/theme-dark/components/list";
import listItem from "app/themes/theme-dark/components/list/listItem";
import listItemText from "app/themes/theme-dark/components/list/listItemText";
import menu from "app/themes/theme-dark/components/menu";
import menuItem from "app/themes/theme-dark/components/menu/menuItem";
import popover from "app/themes/theme-dark/components/popover";
import sidenav from "app/themes/theme-dark/components/sidenav";
import slider from "app/themes/theme-dark/components/slider";
import stepper from "app/themes/theme-dark/components/stepper";
import step from "app/themes/theme-dark/components/stepper/step";
import stepConnector from "app/themes/theme-dark/components/stepper/stepConnector";
import stepIcon from "app/themes/theme-dark/components/stepper/stepIcon";
import stepLabel from "app/themes/theme-dark/components/stepper/stepLabel";
import svgIcon from "app/themes/theme-dark/components/svgIcon";
import tableCell from "app/themes/theme-dark/components/table/tableCell";
import tableContainer from "app/themes/theme-dark/components/table/tableContainer";
import tableHead from "app/themes/theme-dark/components/table/tableHead";
import tabs from "app/themes/theme-dark/components/tabs";
import tab from "app/themes/theme-dark/components/tabs/tab";
import tooltip from "app/themes/theme-dark/components/tooltip";

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...flatpickr,
        ...container
      }
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: { ...textField },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions }
  }
});
